import * as Sentry from '@sentry/react'
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { useEffect, useRef, useState, type FC } from 'react'
import { type FallbackProps } from 'react-error-boundary'

import { Button } from '@/components/elements'
import { ErrorPage } from '@/components/pages'
import { LABELS, ERROR_MESSAGES } from '@/constants/common/message.constants'

type Props = ErrorComponentProps | FallbackProps

export const DefaultErrorPage: FC<Props> = ({ error }) => {
  // ロード完了前に同一リンクへ遷移しようとした場合に発生する CanceledErrorは router を無効化
  const isCanceledError = error.name === 'CanceledError'
  const router = useRouter()
  const [eventId, setEventId] = useState<string | null>(null)
  const sentErrorRef = useRef<unknown>(null)

  useEffect(() => {
    if (isCanceledError) {
      router.invalidate()
    } else if (sentErrorRef.current !== error) {
      const id = Sentry.captureException(error)
      setEventId(id)
      sentErrorRef.current = error
    }
  }, [isCanceledError, router, error])

  if (isAxiosError(error)) {
    switch (error.response?.status) {
      case 400:
        return <ErrorPage code={400} />
      case 403:
        return <ErrorPage code={403} />
      case 404:
        return <ErrorPage code={404} />
      default:
      // do nothing
    }
  }

  if (isCanceledError) {
    return null
  }

  return (
    <div className="flex h-full flex-col items-center justify-center">
      <div className="text-center">
        <div className="text-4xl font-extrabold text-gray-300">
          {ERROR_MESSAGES.ERROR_IN_ROUTER}
        </div>
        <div className="whitespace-pre-wrap py-5 text-base font-extrabold">
          {ERROR_MESSAGES.ERROR_IN_ROUTER_DESCRIPTION}
        </div>
      </div>
      <div className="mt-4 flex gap-4">
        <Button onClick={() => window.location.reload()}>
          {LABELS.RELOAD}
        </Button>
        <Button type="primary" onClick={() => router.navigate({ to: '/' })}>
          {LABELS.BACK_TO_TOP}
        </Button>
      </div>
      {eventId && (
        <div className="mt-8 whitespace-pre-wrap text-gray-400">
          {ERROR_MESSAGES.ERROR_IN_ROUTER_EVENT_ID_DESCRIPTION} {eventId}
        </div>
      )}
    </div>
  )
}
